import { t, Instance, flow } from 'mobx-state-tree';
import { mediaApi } from '@/api/mediaWrapper';
import { WeTreeApiError } from '@/api/errors';
import type {
  MediaExportCreateResponse,
  MediaExportDownloadResponse,
  MediaExportJobResponse
} from 'we-tree-api-client/v2';

/**
 * MediaStore — orchestration for media library mutations (delete, bulk export).
 * Listing remains screen-local until a fuller library store is needed.
 */
export const MediaStore = t
  .model('MediaStore', {
    deletingMediaAssetId: t.maybeNull(t.string),
    exportJobId: t.maybeNull(t.string),
    exportStatus: t.maybeNull(t.string),
    exportBusy: t.optional(t.boolean, false),
    lastError: t.maybeNull(t.string)
  })
  .actions((self) => ({
    clearError() {
      self.lastError = null;
    },

    clearExportState() {
      self.exportJobId = null;
      self.exportStatus = null;
      self.exportBusy = false;
      self.lastError = null;
    },

    deleteMediaAsset: flow(function* (familyId: string, mediaAssetId: string) {
      self.deletingMediaAssetId = mediaAssetId;
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof mediaApi.deleteMediaAsset>> = yield mediaApi.deleteMediaAsset(
          familyId,
          mediaAssetId
        );
        if (result.success) {
          return { success: true as const };
        }
        const err = result.error;
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to delete media.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete media.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      } finally {
        self.deletingMediaAssetId = null;
      }
    }),

    requestMediaExport: flow(function* (familyId: string) {
      self.exportBusy = true;
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof mediaApi.createMediaExport>> =
          yield mediaApi.createMediaExport(familyId);
        if (result.success) {
          const data = result.data as MediaExportCreateResponse;
          self.exportJobId = data.exportJobId;
          self.exportStatus = data.status;
          return { success: true as const, data };
        }
        const err = result.error;
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to start media export.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to start media export.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      } finally {
        self.exportBusy = false;
      }
    }),

    getMediaExportStatus: flow(function* (familyId: string, exportJobId: string) {
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof mediaApi.getMediaExportStatus>> = yield mediaApi.getMediaExportStatus(
          familyId,
          exportJobId
        );
        if (result.success) {
          const data = result.data as MediaExportJobResponse;
          self.exportJobId = data.exportJobId;
          self.exportStatus = data.status;
          return { success: true as const, data };
        }
        const err = result.error;
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to load export status.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load export status.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      }
    }),

    getMediaExportDownloadUrl: flow(function* (familyId: string, exportJobId: string) {
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof mediaApi.getMediaExportDownloadUrl>> =
          yield mediaApi.getMediaExportDownloadUrl(familyId, exportJobId);
        if (result.success) {
          return { success: true as const, data: result.data as MediaExportDownloadResponse };
        }
        const err = result.error;
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to get download URL.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to get download URL.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      }
    })
  }));

export type MediaStoreType = Instance<typeof MediaStore>;

export const mediaStore = MediaStore.create({
  deletingMediaAssetId: null,
  exportJobId: null,
  exportStatus: null,
  exportBusy: false,
  lastError: null
});
